/* eslint-disable react-hooks/exhaustive-deps */
import { useId, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import Select from "../../../../components/SearchableSelect";
import { submitRequestDialogStyles as styles } from "../../styles";
import { Clear } from "@mui/icons-material";

const ACCEPTED_SCRIPT_EXTENSIONS = ["sh", "sql"];

const formatFileSize = (bytes) =>
  bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;

// One row of the SCRIPTS repeater: pick a common script or upload a custom
// one, then cloud + reboot time, with a remove (×) in the head row.
const ScriptItem = ({
  listAllCommonScriptsResp,
  scriptDetails,
  setScriptDetails,
  deleteScript,
}) => {
  const inputId = useId();
  const [fileSize, setFileSize] = useState(null);
  const [fileError, setFileError] = useState("");

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED_SCRIPT_EXTENSIONS.includes(extension)) {
      setFileError(`Unsupported file type .${extension} — use .sh or .sql`);
      return;
    }
    const fileContent = await file.text();
    setFileSize(file.size);
    setFileError("");
    setScriptDetails({
      ...scriptDetails,
      scriptName: file.name,
      scriptContent: fileContent,
    });
  };

  return (
    <Box sx={styles.scriptRow(Boolean(fileError))}>
      <Box sx={styles.scriptRowHead}>
        {scriptDetails?.customScript ? (
          <>
            <Typography sx={styles.scriptRowName}>
              {scriptDetails?.scriptName || "No file selected"}
            </Typography>
            {scriptDetails?.scriptName && fileSize != null && (
              <Typography sx={styles.scriptRowMeta}>
                {formatFileSize(fileSize)}
              </Typography>
            )}
            <input
              accept=".sh,.sql"
              type="file"
              id={inputId}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <label htmlFor={inputId}>
              <Button
                size="small"
                variant="outlined"
                component="span"
                sx={styles.uploadButton}
              >
                {scriptDetails?.scriptName ? "Replace" : "Choose file"}
              </Button>
            </label>
          </>
        ) : (
          <Select
            fullWidth
            size="small"
            displayEmpty
            sx={styles.tenantSelect}
            value={scriptDetails?.scriptName || ""}
            renderValue={(value) => value || "Select script"}
            onChange={(e) =>
              setScriptDetails({
                ...scriptDetails,
                scriptName: e.target.value,
              })
            }
          >
            {listAllCommonScriptsResp?.scripts?.map((script) => (
              <MenuItem value={script} key={script}>
                {script}
              </MenuItem>
            ))}
          </Select>
        )}
        <IconButton size="small" onClick={() => deleteScript()}>
          <Clear fontSize="small" />
        </IconButton>
      </Box>
      {fileError && (
        <Typography sx={styles.uploadError}>{fileError}</Typography>
      )}
      <Box sx={styles.scriptRowControls}>
        <Box>
          <FormLabel sx={styles.fieldLabel}>Cloud</FormLabel>
          <Select
            fullWidth
            size="small"
            sx={styles.tenantSelect}
            value={scriptDetails?.cloud || "ALL"}
            onChange={(e) => {
              setScriptDetails({
                ...scriptDetails,
                cloud: e.target.value,
              });
            }}
          >
            {["ALL", "AWS", "AZURE"].map((cloud, i) => (
              <MenuItem value={cloud} key={i}>
                {cloud}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <FormLabel sx={styles.fieldLabel}>
            Reboot time (minutes, 0 = no reboot)
          </FormLabel>
          <TextField
            fullWidth
            size="small"
            sx={styles.fieldInput}
            value={scriptDetails?.rebootTime ?? 0}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (isNaN(val)) return;
              if (val < 0) return;
              setScriptDetails({
                ...scriptDetails,
                rebootTime: val,
              });
            }}
          />
        </Box>
        <FormControlLabel
          sx={styles.scriptCustomToggle}
          control={
            <Checkbox
              size="small"
              checked={scriptDetails?.customScript || false}
              onChange={() => {
                setFileSize(null);
                setFileError("");
                setScriptDetails({
                  customScript: !scriptDetails?.customScript,
                  cloud: "ALL",
                  rebootTime: 0,
                  scriptName: "",
                });
              }}
            />
          }
          label="Custom script"
        />
      </Box>
    </Box>
  );
};

export default ScriptItem;
